import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  deleteDoc,
} from 'firebase/firestore';
import moment from 'moment';
import {
  NotificationsParticipants,
  ReminderNotification,
  Ride,
  UserInRide,
} from '../../interfaces';
import { NotificationType } from '../../enums';
import { functions, db, dataStore } from '../../app.component';
import { httpsCallable } from 'firebase/functions';
import { valueToDate } from '../../globals';

export async function planNotificationParticipant(user: UserInRide, orgId: string) {
  const organisationNotificationSettings = getOrganisationNotificationSettings();
  const userNotificationSettings = await getUserNotificationSettings(user.id, orgId);
  if (!user.smsNotificationPhoneNumber && userNotificationSettings?.smsNotificationPhoneNumber) {
    user.smsNotificationPhoneNumber = userNotificationSettings.smsNotificationPhoneNumber;
  }
  const sendMail =
    user.email &&
    organisationNotificationSettings.email.enabled &&
    organisationNotificationSettings.email.RideConfirmed &&
    userNotificationSettings.email.enabled &&
    userNotificationSettings.email.RideConfirmed;
  const sendSMS =
    user.smsNotificationPhoneNumber &&
    organisationNotificationSettings.sms.enabled &&
    organisationNotificationSettings.sms.RideConfirmed &&
    userNotificationSettings.sms.enabled &&
    userNotificationSettings.sms.RideConfirmed;
  const from = typeof user.from === 'string' ? user.from : (user.from?.address ?? '');
  const to = typeof user.to === 'string' ? user.to : (user.to?.address ?? '');
  const dateString = moment(valueToDate(user.userPickupTime)).format('DD-MM-YYYY');
  const startTime = moment(valueToDate(user.userPickupTime)).format('HH:mm');
  const endTime = moment(valueToDate(user.userDropoffTime)).format('HH:mm');

  try {
    if (sendMail) {
      const callBody = {
        orgId: orgId,
        userName: user.title,
        mailType: 'rideConfirmation',
        emailTo: user.email,
        optionalData: {
          dateString: dateString,
          startTime: startTime,
          endTime: endTime,
          startLocation: from,
          endLocation: to,
        },
      };
      const callable = httpsCallable(functions, 'httpSendMail');
      await callable(callBody);
    }

    if (sendSMS) {
      const callBody = {
        messageToSend: `Hierbij ontvang je de informatie voor je rit. Je rit vindt plaats op ${dateString} van ${startTime} tot ${endTime}. Je wordt vervoerd van ${from} naar ${to}.`,
        phoneTo: user?.smsNotificationPhoneNumber,
      };
      const callable = httpsCallable(functions, 'smsSendToCentral');
      await callable(callBody);
    }
  } catch (error) {
    console.log(error);
  }
}

export async function changedRideNotificationParticipant(orgId: string, user: UserInRide) {
  const organisationNotificationSettings = getOrganisationNotificationSettings();
  const userNotificationSettings = await getUserNotificationSettings(user.id, orgId);
  if (!user.smsNotificationPhoneNumber && userNotificationSettings?.smsNotificationPhoneNumber) {
    user.smsNotificationPhoneNumber = userNotificationSettings.smsNotificationPhoneNumber;
  }
  const sendMail =
    user.email &&
    organisationNotificationSettings.email.enabled &&
    organisationNotificationSettings.email.RideChanged &&
    userNotificationSettings?.email.enabled &&
    userNotificationSettings?.email.RideChanged;
  const sendSMS =
    user.smsNotificationPhoneNumber &&
    organisationNotificationSettings.sms.enabled &&
    organisationNotificationSettings.sms.RideChanged &&
    userNotificationSettings?.sms.enabled &&
    userNotificationSettings?.sms.RideChanged;

  const from = typeof user.from === 'string' ? user.from : (user.from?.address ?? '');
  const to = typeof user.to === 'string' ? user.to : (user.to?.address ?? '');
  const dateString = moment(valueToDate(user.userPickupTime)).format('DD-MM-YYYY');
  const startTime = moment(valueToDate(user.userPickupTime)).format('HH:mm');
  const endTime = moment(valueToDate(user.userDropoffTime)).format('HH:mm');

  try {
    if (sendMail) {
      const callBody = {
        orgId: orgId,
        userName: user.title,
        mailType: 'rideChanged',
        emailTo: user.email,
        optionalData: {
          dateString: dateString,
          startTime: startTime,
          endTime: endTime,
          startLocation: from,
          endLocation: to,
        },
      };
      const callable = httpsCallable(functions, 'httpSendMail');
      await callable(callBody);
    }
    if (sendSMS) {
      const callBody = {
        messageToSend: `Hierbij ontvang je de gewijzigde informatie voor je rit. Je rit vindt plaats op ${dateString} van ${startTime} tot ${endTime}. Je wordt vervoerd van ${from} naar ${to}.`,
        phoneTo: user?.smsNotificationPhoneNumber,
      };
      const callable = httpsCallable(functions, 'smsSendToCentral');
      await callable(callBody);
    }
  } catch (error) {
    console.log(error);
  }
}

export async function deleteRideNotificationParticipant(user: UserInRide, orgId: string) {
  const organisationNotificationSettings = getOrganisationNotificationSettings();
  const userNotificationSettings = await getUserNotificationSettings(user.id, orgId);
  if (!user.smsNotificationPhoneNumber && userNotificationSettings?.smsNotificationPhoneNumber) {
    user.smsNotificationPhoneNumber = userNotificationSettings.smsNotificationPhoneNumber;
  }
  const sendMail =
    user.email &&
    organisationNotificationSettings.email.enabled &&
    organisationNotificationSettings.email.RideDeleted &&
    userNotificationSettings?.email.enabled &&
    userNotificationSettings?.email.RideDeleted;
  const sendSMS =
    user.smsNotificationPhoneNumber &&
    organisationNotificationSettings.sms.enabled &&
    organisationNotificationSettings.sms.RideDeleted &&
    userNotificationSettings?.sms.enabled &&
    userNotificationSettings?.sms.RideDeleted;

  const from = typeof user.from === 'string' ? user.from : (user.from?.address ?? '');
  const to = typeof user.to === 'string' ? user.to : (user.to?.address ?? '');
  const dateString = moment(valueToDate(user.userPickupTime)).format('DD-MM-YYYY');
  const startTime = moment(valueToDate(user.userPickupTime)).format('HH:mm');
  const endTime = moment(valueToDate(user.userDropoffTime)).format('HH:mm');

  try {
    if (sendMail) {
      const callBody = {
        orgId: orgId,
        userName: user.title,
        mailType: 'rideDeleted',
        emailTo: user.email,
        optionalData: {
          dateString: dateString,
          startTime: startTime,
          endTime: endTime,
          startLocation: from,
          endLocation: to,
        },
      };
      const callable = httpsCallable(functions, 'httpSendMail');
      await callable(callBody);
    }

    if (sendSMS) {
      const callBody = {
        messageToSend: `Je rit op ${dateString} van ${startTime} tot ${endTime} is geannuleerd. Je zou vervoerd worden van ${from} naar ${to}. `,
        phoneTo: user?.smsNotificationPhoneNumber,
      };
      const callable = httpsCallable(functions, 'smsSendToCentral');
      await callable(callBody);
    }
  } catch (error) {
    console.log(error);
  }
}

export async function addToReminderNotificationQueue(
  rideId: string,
  user: UserInRide,
  orgId: string,
  checkExisting: boolean = true,
) {
  const organisationNotificationSettings = getOrganisationNotificationSettings();
  const userNotificationSettings = await getUserNotificationSettings(user.id, orgId);
  const from = typeof user.from === 'string' ? user.from : (user.from?.address ?? '');
  const to = typeof user.to === 'string' ? user.to : (user.to?.address ?? '');
  const date = valueToDate(user.userPickupTime);
  const dateToSendFromEmail = moment(date)
    .subtract(organisationNotificationSettings.email.RideReminderAmount, 'minutes')
    .toDate();
  const dateToSendFromSMS = moment(date)
    .subtract(organisationNotificationSettings.sms.RideReminderAmount, 'minutes')
    .toDate();

  const userObj = {
    id: user.id,
    startRideLocation: from,
    endRideLocation: to,
    pickupTime: date,
    dropoffTime: valueToDate(user.userDropoffTime),
  };
  if (user.email) {
    userObj['email'] = user.email;
  }
  if (user.title) {
    userObj['name'] = user.title;
  }
  if (user?.smsNotificationPhoneNumber || userNotificationSettings?.smsNotificationPhoneNumber) {
    userObj['smsNotificationPhoneNumber'] =
      user.smsNotificationPhoneNumber ?? userNotificationSettings.smsNotificationPhoneNumber;
  }
  userObj['notifyByEmail'] =
    (userObj['email'] &&
      organisationNotificationSettings.email.enabled &&
      organisationNotificationSettings.email.RideReminder &&
      userNotificationSettings?.email.enabled &&
      userNotificationSettings?.email.RideReminder) ??
    false;
  userObj['notifyBySMS'] =
    (userObj['smsNotificationPhoneNumber'] &&
      organisationNotificationSettings.sms.enabled &&
      organisationNotificationSettings.sms.RideReminder &&
      userNotificationSettings?.sms.enabled &&
      userNotificationSettings?.sms.RideReminder) ??
    false;

  if (!userObj['notifyByEmail'] && !userObj['notifyBySMS']) {
    return;
  }
  let queueDocs = [];
  if (checkExisting) {
    queueDocs = (
      await getDocs(
        query(
          collection(db, `organisations/${orgId}/queue`),
          where('rideId', '==', rideId),
          where('orgId', '==', orgId),
          where('type', '==', NotificationType.Reminder),
          where('user.id', '==', user.id),
        ),
      )
    ).docs;
  }
  if (queueDocs.length > 0) {
    const saveObj = {
      ...queueDocs[0].data(),
      id: queueDocs[0].id,
      dateToSendFromEmail: dateToSendFromEmail,
      dateToSendFromSMS: dateToSendFromSMS,
      user: userObj,
    } as ReminderNotification;
    setDoc(doc(db, `organisations/${orgId}/queue/${saveObj.id}`), saveObj, {
      merge: true,
    });
  } else {
    const docId = doc(collection(db, `organisations/${orgId}/queue`)).id;
    const saveObj = {
      id: docId,
      orgId: orgId,
      dateToSendFromEmail: dateToSendFromEmail,
      dateToSendFromSMS: dateToSendFromSMS,
      notificationSendEmail: false,
      notificationSendSMS: false,
      type: NotificationType.Reminder,
      rideId: rideId,
      user: userObj,
    } as ReminderNotification;
    setDoc(doc(db, `organisations/${orgId}/queue/${docId}`), saveObj, {
      merge: true,
    });
  }
}

export async function changeReminderInQueue(rideId: string, user: UserInRide, orgId: string) {
  const queueDocs = (
    await getDocs(
      query(
        collection(db, `organisations/${orgId}/queue`),
        where('rideId', '==', rideId),
        where('orgId', '==', orgId),
        where('type', '==', NotificationType.Reminder),
        where('user.id', '==', user.id),
      ),
    )
  ).docs;
  const organisationNotificationSettings = getOrganisationNotificationSettings();
  if (queueDocs.length > 0) {
    const saveObj = {
      ...queueDocs[0].data(),
      id: queueDocs[0].id,
    } as ReminderNotification;
    saveObj.dateToSendFromEmail = moment(valueToDate(user.userPickupTime))
      .subtract(organisationNotificationSettings.email.RideReminderAmount, 'minutes')
      .toDate();

    saveObj.dateToSendFromSMS = moment(valueToDate(user.userPickupTime))
      .subtract(organisationNotificationSettings.sms.RideReminderAmount, 'minutes')
      .toDate();
    saveObj.user.pickupTime = valueToDate(user.userPickupTime);
    saveObj.user.dropoffTime = valueToDate(user.userDropoffTime);
    saveObj.user.startRideLocation =
      typeof user.from === 'string' ? user.from : (user.from?.address ?? '');
    saveObj.user.endRideLocation = typeof user.to === 'string' ? user.to : (user.to?.address ?? '');
    setDoc(doc(db, `organisations/${orgId}/queue/${saveObj.id}`), saveObj, {
      merge: true,
    });
  } else {
    await addToReminderNotificationQueue(rideId, user, orgId, false);
  }
}

export async function removeFromReminderQueue(rideId: string, user: UserInRide, orgId: string) {
  const queueData = (
    await getDocs(
      query(
        collection(db, `organisations/${orgId}/queue`),
        where('rideId', '==', rideId),
        where('orgId', '==', orgId),
        where('type', '==', NotificationType.Reminder),
        where('user.id', '==', user.id),
      ),
    )
  ).docs;

  if (queueData.length > 0) {
    await deleteDoc(doc(db, `organisations/${orgId}/queue/${queueData[0].id}`));
  }
}

async function getUserNotificationSettings(userId: string, orgId: string) {
  const userData = (await getDoc(doc(db, `organisations/${orgId}/users/${userId}`))).data();
  const email: NotificationsParticipants = {
    enabled: userData?.notifyByEmail ?? false,
    RideConfirmed: userData?.notifyByEmailRideConfirmed ?? false,
    RideChanged: userData?.notifyByEmailRideChanged ?? false,
    RideDeleted: userData?.notifyByEmailRideDeleted ?? false,
    RideReminder: userData?.notifyByEmailRideReminder ?? false,
  };
  const sms: NotificationsParticipants = {
    enabled: userData?.notifyBySMS ?? false,
    RideConfirmed: userData?.notifyBySMSRideConfirmed ?? false,
    RideChanged: userData?.notifyBySMSRideChanged ?? false,
    RideDeleted: userData?.notifyBySMSRideDeleted ?? false,
    RideReminder: userData?.notifyBySMSRideReminder ?? false,
  };
  return { email, sms, smsNotificationPhoneNumber: userData?.smsNotificationPhoneNumber };
}

export function getOrganisationNotificationSettings() {
  const usesUserNotificationSystem =
    dataStore.paidFunctionalities?.usesUserNotificationSystem ?? false;
  const email: NotificationsParticipants = {
    enabled:
      usesUserNotificationSystem &&
      (dataStore.generalSettings?.emailNotificationsParticipants ?? false),
    RideConfirmed: dataStore.generalSettings?.emailNotificationsParticipantsRideConfirmed ?? false,
    RideChanged: dataStore.generalSettings?.emailNotificationsParticipantsRideChanged ?? false,
    RideDeleted: dataStore.generalSettings?.emailNotificationsParticipantsRideDeleted ?? false,
    RideReminder: dataStore.generalSettings?.emailNotificationsParticipantsRideReminder ?? false,
    RideReminderAmount:
      dataStore.generalSettings?.emailNotificationsParticipantsRideReminderAmount ?? 5,
  };
  const sms: NotificationsParticipants = {
    enabled:
      usesUserNotificationSystem &&
      (dataStore.generalSettings?.smsNotificationsParticipants ?? false),
    RideConfirmed: dataStore.generalSettings?.smsNotificationsParticipantsRideConfirmed ?? false,
    RideChanged: dataStore.generalSettings?.smsNotificationsParticipantsRideChanged ?? false,
    RideDeleted: dataStore.generalSettings?.smsNotificationsParticipantsRideDeleted ?? false,
    RideReminder: dataStore.generalSettings?.smsNotificationsParticipantsRideReminder ?? false,
    RideReminderAmount:
      dataStore.generalSettings?.smsNotificationsParticipantsRideReminderAmount ?? 5,
  };
  return { email, sms };
}
